/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Flood / Hydrometric-Gauge family (operator-cop-
 *  doctrine §3A.7, §8.4; §2.7 flood detect). 2026-06-04.
 *
 *  The SINGLE source of the Hydrometric Gauges layer's rate-of-rise band palette
 *  (Flood Risk group). Consumed ONLY by the gauge family:
 *    - the HydrometricGaugesLayer map dots (ambient, sized + coloured by band)
 *    - the HydrometricGaugeBody side-panel band chip
 *
 *  The signal is the SUSTAINED rate-of-rise (cm/hr) — TSR-derived, datum-free
 *  (recon §C: Canada publishes NO official flood stage; water level is station-
 *  local ASSUMED datum). The bands mirror the fusion thresholds (flood-anomaly.ts):
 *    steady  rate < 2.5         · teal   #2dd4bf  (calm / falling)
 *    elevated 2.5 ≤ rate < 5    · yellow #facc15  (climbing, below the watch trip)
 *    rising  5 ≤ rate < 10      · orange #fb923c  (WATCH — a flood anomaly trips here)
 *    rapid   rate ≥ 10          · violet #c026d3  (WARNING — rapid rise; Colour Law 5
 *                                                  retune off rose to kill the 4th red)
 *  An unknown/level-only gauge (rate null) is grey #94a3b8.
 *
 *  Overlap debt this lock resolves (§3A.7): teal/yellow/orange/rose are deliberately
 *  DISTINCT hexes from the Response-Level family (grey/blue/amber/red), so a falling
 *  gauge's calm teal never reads as a Response Level and a flood anomaly's scored RL
 *  dot (responseLevel palette + 🌊 glyph) stays the urgent signal. Colour is never
 *  load-bearing alone: every gauge dot is paired with the rate value + band label in
 *  the panel, and a hollow ring marks a gauge-sparse station.
 *
 *  ⛔ Do NOT import another family's palette here (enforced by the
 *     no-restricted-imports lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

export type RateBand = 'steady' | 'elevated' | 'rising' | 'rapid' | 'unknown';

/** Band thresholds (cm/hr) — kept in lockstep with the fusion constants in
 *  apps/ingestion flood-anomaly.ts (RISE=5, RAPID=10, RESOLVE/hold=2.5). */
export const RATE_BAND_ELEVATED_CM_HR = 2.5;
export const RATE_BAND_RISING_CM_HR = 5;
export const RATE_BAND_RAPID_CM_HR = 10;

export interface GaugeBandStyle {
  band: RateBand;
  /** CSS hex for the dot + chip. */
  color: string;
  /** Short label (colour is never load-bearing alone). */
  label: string;
}

const BANDS: Record<RateBand, GaugeBandStyle> = {
  steady: { band: 'steady', color: '#2dd4bf', label: 'Steady / falling' },
  elevated: { band: 'elevated', color: '#facc15', label: 'Elevated' },
  rising: { band: 'rising', color: '#fb923c', label: 'Rising (watch)' },
  rapid: { band: 'rapid', color: '#c026d3', label: 'Rapid rise (warning)' },
  unknown: { band: 'unknown', color: '#94a3b8', label: 'No rate' },
};

/** Resolve the rate-of-rise band for a sustained cm/hr value (null → unknown). A
 *  falling gauge maps to 'steady' (a flood signal is a RISE, never a fall). */
export function rateBand(rateCmPerHr: number | null | undefined): RateBand {
  if (rateCmPerHr === null || rateCmPerHr === undefined || !Number.isFinite(rateCmPerHr)) {
    return 'unknown';
  }
  if (rateCmPerHr >= RATE_BAND_RAPID_CM_HR) return 'rapid';
  if (rateCmPerHr >= RATE_BAND_RISING_CM_HR) return 'rising';
  if (rateCmPerHr >= RATE_BAND_ELEVATED_CM_HR) return 'elevated';
  return 'steady';
}

/** Full style for a gauge reading's rate-of-rise. */
export function gaugeBandStyle(rateCmPerHr: number | null | undefined): GaugeBandStyle {
  return BANDS[rateBand(rateCmPerHr)];
}

/** Ambient dot size by band — a rising/rapid gauge reads slightly larger so the
 *  eye lands on it, but never pulses (motion is reserved for the scored Anomaly
 *  Queue dots, §3B.8 motion budget). */
export function gaugeDotSize(rateCmPerHr: number | null | undefined): number {
  switch (rateBand(rateCmPerHr)) {
    case 'rapid':
      return 13;
    case 'rising':
      return 11;
    case 'elevated':
      return 9;
    default:
      return 7;
  }
}

/** Ambient dot fill opacity. */
export const GAUGE_DOT_ALPHA = 0.9;
